import ClientCommentExternalUser from "components/ClientCommentExternalUser";
import RadioGroupField from "components/controls/RadioGroupField";
import VinOrLicensePlateSearchTextInputField from "components/controls/VinOrLicensePlateTextInputField";
import VinSearchTextInputField from "components/controls/VinSearchTextInputField";
import { vehicleFormClasses } from "pages/vehicle/VehicleForm";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ComboBoxField from "../../../../components/controls/ComboBoxField";
import DatePickerField from "../../../../components/controls/DatePickerField";
import NumberInputField from "../../../../components/controls/NumberInputField";
import TextInputField from "../../../../components/controls/TextInputField";
import { useSelector } from "../../../../store/hooks";
import { UserRole } from "../../../../types/types";
import {
  buildClientCtx,
  isClientFieldMandatory,
  isClientFormUser,
  isClientHiddenVehicleField,
} from "../../clientVehicleSpec";
import {
  hasVehicleBeenActivated,
  isDisabledExternalAndHasBeenActivated,
} from "../../helpers";
import { determineVehicleMainGroupCodeAT } from "../../vehicleMainGroupAT";

function range(size: number, startAt: number = 0) {
  return [...Array(size).keys()].map((i) => i + startAt);
}

const VehicleFormItems = ({ initialValues, vehicle, route, formikProps }) => {
  const { t } = useTranslation();
  const userRole = useSelector((s) => s.auth.userRole);
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings);
  const fleet = useSelector((s) => s.vehicle.fleet);
  const countryCode = fleet?.countryCode;
  const { values, errors, handleChange, setFieldValue } = formikProps;
  const taxonomy = useSelector((s) => s.taxonomy);
  const confidentiality = useSelector(
    (s) => s.vehicle?.scannedFormFields?.confidentialityInfo
  );

  // AT / CZ client (external)
  const isClientForm = isClientFormUser(
    vehicleAppSettings?.COUNTRY_CODE,
    userRole
  );
  // Shared with validation: an asterisk shows exactly when the field is enforced.
  const clientCtx = isClientForm
    ? buildClientCtx(values, taxonomy, vehicleAppSettings?.COUNTRY_CODE)
    : null;
  const req = (fieldName: string) =>
    isClientFieldMandatory(fieldName, clientCtx);

  // AT internal user (motor specialist): the vehicle main group follows the
  // legal vehicle type and the usage code with the same mapping the backend
  // applies for clients, so every type/usage change recalculates it. The user
  // may still override it before saving - until the next type/usage change.
  const isAtInternalUser =
    vehicleAppSettings?.COUNTRY_CODE === "Country.AT" &&
    userRole !== UserRole.external;

  // Helper function to check if field should be visible
  const isFieldVisible = (fieldName: string) => {
    // Client: hide vehicle fields not in the Smart Forms Excel.
    if (
      isClientHiddenVehicleField(
        fieldName,
        vehicleAppSettings?.COUNTRY_CODE,
        userRole
      )
    ) {
      return false;
    }
    return vehicleAppSettings?.VISIBLE_FIELDS?.includes(fieldName) ?? true;
  };

  const isLicensePlateDisabled = ({ vehicle, route }: any) => {
    return (
      hasVehicleBeenActivated({ vehicle, route }) || route === "readOnlyVehicle"
    );
  };

  const vehicleBrandOptions = useMemo(() => {
    return taxonomy.VehicleBrand.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.VehicleBrand.items]);

  const vehicleMainGroupOptions = useMemo(() => {
    return taxonomy.VehicleMainGroup?.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.VehicleMainGroup?.items]);

  const recalculateVehicleMainGroup = useCallback(
    (vehicleTypeOption, carUsageOption) => {
      if (!isAtInternalUser) return;
      const mainGroupCode = determineVehicleMainGroupCodeAT(
        vehicleTypeOption?.value,
        carUsageOption?.value,
        taxonomy
      );
      setFieldValue(
        "vehicleMainGroupCode",
        vehicleMainGroupOptions?.find(
          (option) => option.value === mainGroupCode
        ) ?? null
      );
    },
    [isAtInternalUser, taxonomy, setFieldValue, vehicleMainGroupOptions]
  );

  const vehicleTypeCodeOptions = useMemo(() => {
    return taxonomy.VehicleTypeLegal.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.VehicleTypeLegal.items]);

  const vehicleBodyTypeCodeOptions = useMemo(() => {
    return taxonomy.VehicleBodyType.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.VehicleBodyType.items]);

  const truckMountingCodeOptions = useMemo(() => {
    return taxonomy.TruckMounting.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.TruckMounting.items]);

  const vehicleCarUsageCodeOptions = useMemo(() => {
    const countryObj = taxonomy.Country.byId[countryCode];
    if (!countryObj) {
      return [];
    }
    const country = countryObj.code.split(".")[1];

    const itemsByCountry = taxonomy.VehicleUsageType.items.filter((item) => {
      return item.code.includes(country);
    });

    return itemsByCountry.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [taxonomy.Country.byId, taxonomy.VehicleUsageType.items, countryCode, t]);

  const vehicleClassCodeOptions = useMemo(() => {
    return taxonomy.VehicleClass.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.VehicleClass.items]);

  const vehiclePowerTypeCodeOptions = useMemo(() => {
    return taxonomy.VehiclePowerType.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy.VehiclePowerType.items]);

  const yearOfConstructionOptions = useMemo(() => {
    const options = range(100, new Date().getFullYear() - 100 + 1)
      .reverse()
      .map((year) => ({
        value: year,
        label: year.toString(),
      }));
    return options;
  }, []);

  return (
    <div className={vehicleFormClasses.container}>
      {/* 4 - Car Usage Code */}
      {isFieldVisible("carUsageCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            errors={errors}
            required={req("carUsageCode")}
            name="carUsageCode"
            label={"bfm.vehicleForm.carUsageCode.label"}
            setFieldValue={setFieldValue}
            options={vehicleCarUsageCodeOptions || []}
            defaultValue={values.carUsageCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "carUsageCode"
            )}
            confidentiality={confidentiality?.CarUsageCode}
            onValueChange={(option) =>
              recalculateVehicleMainGroup(values.vehicleTypeCode, option)
            }
          />
        </div>
      )}
      {/* 5 - VIN */}
      {isFieldVisible("vin") && (
        <div className={vehicleFormClasses.item}>
          {vehicleAppSettings?.HAS_SEARCH_BY_VIN ? (
            <VinSearchTextInputField
              value={values.vin}
              errors={errors}
              maxLength={32}
              onChange={handleChange}
              name={"vin"}
              required
              disabled={route === "readOnlyVehicle"}
              label={"bfm.vehicleForm.vin.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes("vin")}
              confidentiality={confidentiality?.Vin}
              setFieldValue={setFieldValue}
            />
          ) : vehicleAppSettings?.HAS_SEARCH_BY_LICENSE_PLATE_OR_VIN ? (
            <VinOrLicensePlateSearchTextInputField
              value={values.vin}
              errors={errors}
              maxLength={32}
              onChange={handleChange}
              name={"vin"}
              required
              disabled={route === "readOnlyVehicle"}
              label={"bfm.vehicleForm.vin.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes("vin")}
              confidentiality={confidentiality?.Vin}
              setFieldValue={setFieldValue}
            />
          ) : (
            <TextInputField
              value={values.vin}
              errors={errors}
              maxLength={32}
              onChange={handleChange}
              name={"vin"}
              required
              disabled={route === "readOnlyVehicle"}
              label={"bfm.vehicleForm.vin.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes("vin")}
              confidentiality={confidentiality?.Vin}
            />
          )}
        </div>
      )}
      {/* 6 - First Registration Date */}
      {isFieldVisible("firstRegistrationDate") && (
        <div className={vehicleFormClasses.item}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.firstRegistrationDate}
            required={req("firstRegistrationDate")}
            name="firstRegistrationDate"
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.firstRegistrationDate.label"}
            placeholder={t("bfm.selectDate")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "firstRegistrationDate"
            )}
            confidentiality={confidentiality?.FirstRegistrationDate}
          />
        </div>
      )}
      {/* 7 */}
      {isFieldVisible("yearOfConstruction") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={route === "readOnlyVehicle"}
            errors={errors}
            name="yearOfConstruction"
            label={"bfm.vehicleForm.yearOfConstruction.label"}
            setFieldValue={setFieldValue}
            options={yearOfConstructionOptions || []}
            defaultValue={values.yearOfConstruction}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "yearOfConstruction"
            )}
            confidentiality={confidentiality?.YearOfConstruction}
          />
        </div>
      )}
      {/* 8 */}
      {isFieldVisible("nationalCodeId") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.nationalCodeId}
            errors={errors}
            maxLength={254}
            onChange={handleChange}
            name={"nationalCodeId"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.nationalCodeId.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "nationalCodeId"
            )}
            confidentiality={confidentiality?.NationalCodeId}
          />
        </div>
      )}
      {/* 9 */}
      {isFieldVisible("vehicleClassCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={route === "readOnlyVehicle"}
            errors={errors}
            required={req("vehicleClassCode")}
            name="vehicleClassCode"
            label={"bfm.vehicleForm.vehicleClassCode.label"}
            setFieldValue={setFieldValue}
            options={vehicleClassCodeOptions || []}
            defaultValue={values.vehicleClassCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleClassCode"
            )}
            confidentiality={confidentiality?.VehicleClassCode}
          />
        </div>
      )}
      {vehicleAppSettings?.COUNTRY_CODE === "Country.LT" && (
        <div className={vehicleFormClasses.item}></div>
      )}
      {/* 11 - placed before the main group, which is derived from it */}
      {isFieldVisible("vehicleTypeCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            errors={errors}
            name="vehicleTypeCode"
            label={"bfm.vehicleForm.vehicleTypeCode.label"}
            setFieldValue={setFieldValue}
            options={vehicleTypeCodeOptions || []}
            defaultValue={values.vehicleTypeCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleTypeCode"
            )}
            required
            confidentiality={confidentiality?.VehicleTypeCode}
            onValueChange={(option) =>
              recalculateVehicleMainGroup(option, values.carUsageCode)
            }
          />
        </div>
      )}
      {/* 10 */}
      {isFieldVisible("vehicleMainGroupCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            errors={errors}
            name="vehicleMainGroupCode"
            label={"bfm.vehicleForm.vehicleMainGroupCode.label"}
            setFieldValue={setFieldValue}
            options={vehicleMainGroupOptions || []}
            defaultValue={values.vehicleMainGroupCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleMainGroupCode"
            )}
            required
            confidentiality={confidentiality?.VehicleMainGroupCode}
          />
        </div>
      )}
      {/* 11 a */}
      {isFieldVisible("vehicleBodyTypeCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            errors={errors}
            name="vehicleBodyTypeCode"
            label={"bfm.vehicleForm.vehicleBodyTypeCode.label"}
            setFieldValue={setFieldValue}
            options={vehicleBodyTypeCodeOptions || []}
            defaultValue={values.vehicleBodyTypeCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleBodyTypeCode"
            )}
            confidentiality={confidentiality?.VehicleBodyTypeCode}
          />
        </div>
      )}
      {/* 11 b */}
      {isFieldVisible("truckMountingCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            errors={errors}
            name="truckMountingCode"
            label={"bfm.vehicleForm.truckMountingCode.label"}
            setFieldValue={setFieldValue}
            options={truckMountingCodeOptions || []}
            defaultValue={values.truckMountingCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "truckMountingCode"
            )}
            confidentiality={confidentiality?.TruckMountingCode}
          />
        </div>
      )}
      {/* 12 */}
      {isFieldVisible("brandCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={route === "readOnlyVehicle"}
            errors={errors}
            required={req("brandCode")}
            name="brandCode"
            label={"bfm.vehicleForm.brandCode.label"}
            setFieldValue={setFieldValue}
            options={vehicleBrandOptions || []}
            defaultValue={values.brandCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "brandCode"
            )}
            confidentiality={confidentiality?.BrandCode}
          />
        </div>
      )}
      {/* 13 */}
      {isFieldVisible("model") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.model}
            errors={errors}
            maxLength={254}
            onChange={handleChange}
            required={req("model")}
            name={"model"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.model.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes("model")}
            confidentiality={confidentiality?.Model}
          />
        </div>
      )}
      {/* 14 */}
      {isFieldVisible("maxGrossWeightKg") && (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.maxGrossWeightKg}
            errors={errors}
            maxLength={10}
            onChange={handleChange}
            required={req("maxGrossWeightKg")}
            name={"maxGrossWeightKg"}
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.maxGrossWeightKg.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "maxGrossWeightKg"
            )}
            confidentiality={confidentiality?.MaxGrossWeightKg}
          />
        </div>
      )}
      {isFieldVisible("emptyWeightKg") && (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.emptyWeightKg}
            errors={errors}
            maxLength={10}
            onChange={handleChange}
            required={req("emptyWeightKg")}
            name={"emptyWeightKg"}
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.emptyWeightKg.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "emptyWeightKg"
            )}
            confidentiality={confidentiality?.EmptyWeightKg}
          />
        </div>
      )}
      {/* 15 */}
      {isFieldVisible("weightCapacityKg") && (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.weightCapacityKg}
            errors={errors}
            maxLength={10}
            onChange={handleChange}
            required={req("weightCapacityKg")}
            name={"weightCapacityKg"}
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.weightCapacityKg.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "weightCapacityKg"
            )}
            confidentiality={confidentiality?.WeightCapacityKg}
          />
        </div>
      )}
      {/* 16 */}
      {isFieldVisible("seats") && (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.seats}
            errors={errors}
            maxLength={254}
            onChange={handleChange}
            required={req("seats")}
            name={"seats"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.seats.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes("seats")}
            confidentiality={confidentiality?.Seats}
          />
        </div>
      )}
      {/* 16 */}
      {isFieldVisible("vehiclePowerTypeCode") && (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            errors={errors}
            required={req("vehiclePowerTypeCode")}
            name="vehiclePowerTypeCode"
            label={"bfm.vehicleForm.vehiclePowerTypeCode.label"}
            setFieldValue={setFieldValue}
            options={vehiclePowerTypeCodeOptions || []}
            defaultValue={values.vehiclePowerTypeCode}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehiclePowerTypeCode"
            )}
            confidentiality={confidentiality?.VehiclePowerTypeCode}
          />
        </div>
      )}{" "}
      {isFieldVisible("isPluginHybrid") && (
        <div className={vehicleFormClasses.item}>
          <RadioGroupField
            checked={values?.isPluginHybrid}
            errors={errors}
            setFieldValue={setFieldValue}
            name={"isPluginHybrid"}
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={t("bfm.vehicleForm.isPluginHybrid.label")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "isPluginHybrid"
            )}
          />
        </div>
      )}
      {/* 17 */}
      {isFieldVisible("cubicCapacityCcm") && (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.cubicCapacityCcm}
            errors={errors}
            maxLength={10}
            onChange={handleChange}
            required={req("cubicCapacityCcm")}
            name={"cubicCapacityCcm"}
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.cubicCapacityCcm.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "cubicCapacityCcm"
            )}
            confidentiality={confidentiality?.CubicCapacityCcm}
          />
        </div>
      )}
      {/* 18 */}
      {isFieldVisible("powerKw") && (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            setFieldValue={setFieldValue}
            value={values.powerKw}
            errors={errors}
            maxLength={10}
            onChange={handleChange}
            required={req("powerKw")}
            name={"powerKw"}
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.powerKw.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes("powerKw")}
            confidentiality={confidentiality?.PowerKw}
          />
        </div>
      )}
      {/* 19 */}
      {isFieldVisible("co2Emission") && (
        <div className={vehicleFormClasses.item}>
          <NumberInputField
            isNegativeAllowed={true}
            setFieldValue={setFieldValue}
            value={values.co2Emission}
            errors={errors}
            maxLength={10}
            onChange={handleChange}
            required={req("co2Emission")}
            name={"co2Emission"}
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.co2Emission.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "co2Emission"
            )}
            confidentiality={confidentiality?.Co2Emission}
          />
        </div>
      )}
      {/* 20 */}
      {isFieldVisible("certificateNumber") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.certificateNumber}
            onChange={handleChange}
            errors={errors}
            name={"certificateNumber"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.certificateNumber.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "certificateNumber"
            )}
            confidentiality={confidentiality?.CertificateNumber}
          />
        </div>
      )}
      {/* 21 */}
      {isFieldVisible("carUsageCodeAdditional") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.carUsageCodeAdditional}
            errors={errors}
            maxLength={254}
            onChange={handleChange}
            name={"carUsageCodeAdditional"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.carUsageCodeAdditional.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "carUsageCodeAdditional"
            )}
            confidentiality={confidentiality?.CarUsageCodeAdditional}
          />
        </div>
      )}
      {/* 22 */}
      {isFieldVisible("secondLicensePlate") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.secondLicensePlate}
            errors={errors}
            maxLength={12}
            onChange={handleChange}
            name={"secondLicensePlate"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.secondLicensePlate.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "secondLicensePlate"
            )}
            confidentiality={confidentiality?.SecondLicensePlate}
          />
        </div>
      )}
      {/* 23 */}
      {isFieldVisible("costCenterClient") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            errors={errors}
            value={values.costCenterClient}
            onChange={handleChange}
            maxLength={254}
            name={"costCenterClient"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.costCenterClient.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "costCenterClient"
            )}
            confidentiality={confidentiality?.CostCenterClient}
          />
        </div>
      )}
      {/* 24 - Client Comment */}
      {isFieldVisible("clientComment") && (
        <div className={vehicleFormClasses.commentItem}>
          {vehicleAppSettings?.HAS_CLIENT_COMMENT_EXTERNAL_USER ? (
            <ClientCommentExternalUser
              value={values.clientCoveragesInfo}
              onChange={(name, value) => setFieldValue(name, value)}
              name={"clientCoveragesInfo"}
              disabled={route === "readOnlyVehicle"}
              label={"bfm.coverages.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "clientCoveragesInfo"
              )}
              errors={errors}
              route={route}
            />
          ) : (
            <TextInputField
              value={values.clientComment}
              onChange={handleChange}
              errors={errors}
              name={"clientComment"}
              multiline
              rows={7}
              disabled={route === "readOnlyVehicle"}
              label={"bfm.vehicleForm.clientComment.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "clientComment"
              )}
              confidentiality={confidentiality?.clientComment}
            />
          )}
        </div>
      )}
      {/* Free Text Fields */}
      {isFieldVisible("freeTextField1") && (
        <div className={vehicleFormClasses.commentItemGrid}>
          <TextInputField
            value={values.freeTextField1}
            onChange={handleChange}
            errors={errors}
            name={"freeTextField1"}
            multiline
            rows={7}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.freeTextField1.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "freeTextField1"
            )}
            confidentiality={confidentiality?.FreeTextField1}
          />
        </div>
      )}
      {isFieldVisible("freeTextField2") && (
        <div className={vehicleFormClasses.commentItemGrid}>
          <TextInputField
            value={values.freeTextField2}
            onChange={handleChange}
            errors={errors}
            name={"freeTextField2"}
            multiline
            rows={7}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.freeTextField2.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "freeTextField2"
            )}
            confidentiality={confidentiality?.FreeTextField2}
          />
        </div>
      )}
    </div>
  );
};

export default VehicleFormItems;
